import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { apiJson, ApiError, BoundSource } from '../../lib/api'
import { PageBody, PageHeader } from '../../components/PageHeader'
import { dateText, dateTimeText, DetailResponse, LegacyPackhouse, LegacyPackhousesResponse, licenceText } from './api'
import styles from './GodMode.module.css'
import { UnauthorisedTagsAlert } from '../../components/UnauthorisedTagsAlert'
import { LegacyImportCard } from './LegacyImportCard'

const KIND: Record<string, string> = {
  created: 'Created',
  product_changed: 'Product changed',
  licence_changed: 'Licence changed',
  legacy_linked: 'Legacy packhouse',
}

const valueText = (v: unknown) => {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'object') return Object.entries(v as Record<string, unknown>).map(([k, x]) => `${k}: ${x ?? 'none'}`).join(', ')
  return String(v)
}

// One client: its details, its product and licence (changed through Client
// Setup's own routes, for this client), its sign-ins, its licence history and
// the changes made to it; for a client from the legacy system, its import
// from Binwise.
export function ClientDetailPage() {
  const { id = '' } = useParams()
  const { user } = useAuth()
  const { subscriptions, selectWorkspace, reloadWorkspaces } = useWorkspace()
  const navigate = useNavigate()
  const [data, setData] = useState<DetailResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [product, setProduct] = useState('')
  const [licence, setLicence] = useState('')
  const [busy, setBusy] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [packhouses, setPackhouses] = useState<LegacyPackhouse[]>([])
  const [legacy, setLegacy] = useState('')
  const [legacyError, setLegacyError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const d = await apiJson<DetailResponse>(user, `/api/god-mode/clients/${id}`)
      setData(d)
      setName(d.client.name)
      setProduct(d.client.product || '')
      setLicence(d.client.licence || '')
      setLegacy(d.client.legacy_packhouse_id == null ? '' : String(d.client.legacy_packhouse_id))
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 404) setNotFound(true)
      else setError(e.message)
    }
  }, [user, id])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    let live = true
    apiJson<LegacyPackhousesResponse>(user, '/api/god-mode/legacy-packhouses')
      .then((d) => live && setPackhouses(d.packhouses))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [user])

  const link = async () => {
    setBusy(true)
    setLegacyError(null)
    try {
      await apiJson(user, `/api/god-mode/clients/${id}/legacy`, { method: 'PUT', body: { legacy_packhouse_id: legacy === '' ? null : Number(legacy) } })
      await load()
      const d = await apiJson<LegacyPackhousesResponse>(user, '/api/god-mode/legacy-packhouses')
      setPackhouses(d.packhouses)
    } catch (e: any) {
      setLegacyError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const rename = async () => {
    setBusy(true)
    setNameError(null)
    try {
      await apiJson(user, `/api/god-mode/clients/${id}/name`, { method: 'PUT', body: { name } })
      await load()
      // The workspace list and switcher show the new name too.
      reloadWorkspaces()
    } catch (e: any) {
      setNameError(e.message)
    } finally {
      setBusy(false)
    }
  }

  // The settings routes act on the workspace X-Tenant-Id names, so each change is pinned to this client.
  const bound: BoundSource | null = user ? { getIdToken: () => user.getIdToken(), uid: user.uid, boundTenantId: id } : null

  const save = async (path: string, body: Record<string, unknown>) => {
    setBusy(true)
    setSaveError(null)
    try {
      await apiJson(bound, path, { method: 'PUT', body })
      await load()
    } catch (e: any) {
      setSaveError(e.message)
    } finally {
      setBusy(false)
    }
  }

  if (notFound) {
    return (
      <>
        <PageHeader title="Client not found" />
        <PageBody>
          <p className={styles.lead}>
            No client of yours has this address. <Link to="/god-mode/clients">Back to clients</Link>
          </p>
        </PageBody>
      </>
    )
  }

  const c = data?.client
  const canOpen = subscriptions.some((s) => s.id === id)

  return (
    <>
      <PageHeader
        title={c?.name || 'Client'}
        actions={
          canOpen && (
            <button
              type="button"
              className={styles.primary}
              onClick={() => {
                selectWorkspace(id)
                navigate('/dashboard')
              }}
              title="Open this client's workspace as its Dataphyll support sign-in"
            >
              Open workspace
            </button>
          )
        }
      />
      <PageBody>
        <div className={styles.stack}>
          <div>
            <Link to="/god-mode/clients" className={styles.secondary}>
              ← All clients
            </Link>{' '}
            <Link to={`/god-mode/clients/${id}/setup`} className={styles.secondary}>
              Setup
            </Link>{' '}
            <Link to={`/god-mode/clients/${id}/cards`} className={styles.secondary}>
              Worker cards
            </Link>
          </div>
          {error && <p className={styles.error}>{error}</p>}
          {!c && !error && <p className={styles.lead}>Loading…</p>}
          {c && data && (
            <>
              <UnauthorisedTagsAlert use={c.unauthorised_tag_use} />
              <section className={styles.card}>
                <h2>Details</h2>
                {nameError && <p className={styles.error}>{nameError}</p>}
                <form
                  className={styles.inline}
                  onSubmit={(e) => {
                    e.preventDefault()
                    rename()
                  }}
                >
                  <label className={styles.field}>
                    Name
                    <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} disabled={busy} />
                  </label>
                  <button
                    type="submit"
                    className={styles.primary}
                    disabled={busy || !name.trim() || name.trim().replace(/\s+/g, ' ') === c.name}
                  >
                    Rename
                  </button>
                </form>
                <dl className={styles.facts}>
                  <div><dt>Product</dt><dd>{c.product_label || 'Not set'}</dd></div>
                  <div><dt>Licence</dt><dd>{c.product === 'packright' ? licenceText(c.licence) : '—'}</dd></div>
                  <div><dt>Licensed since</dt><dd>{dateText(c.licensed_since)}</dd></div>
                  <div><dt>Time zone</dt><dd>{c.timezone || 'Not set'}</dd></div>
                  <div><dt>Region</dt><dd>{c.region_label || c.region || '—'}</dd></div>
                  <div><dt>People</dt><dd>{c.people}</dd></div>
                  <div><dt>Created</dt><dd>{dateText(c.created_at)}</dd></div>
                  <div><dt>Client id</dt><dd><code>{c.id}</code></dd></div>
                </dl>
              </section>

              <section className={styles.card}>
                <h2>Product and licence</h2>
                {saveError && <p className={styles.error}>{saveError}</p>}
                <div className={styles.inline}>
                  <label className={styles.field}>
                    Product
                    <select value={product} onChange={(e) => setProduct(e.target.value)} disabled={busy}>
                      {!c.product && <option value="">Not set</option>}
                      {data.products.map((p) => (
                        <option key={p.k} value={p.k}>{p.label}</option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className={styles.primary}
                    disabled={busy || !product || product === (c.product || '')}
                    onClick={() => save('/api/settings/tenant/product', { product, before: c.product })}
                  >
                    Change product
                  </button>
                </div>
                {c.product === 'packright' && (
                  <div className={styles.inline}>
                    <label className={styles.field}>
                      Stock &amp; Dispatch licence
                      <select value={licence} onChange={(e) => setLicence(e.target.value)} disabled={busy}>
                        <option value="">None</option>
                        {data.licences.map((l) => (
                          <option key={l} value={l}>{licenceText(l)}</option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className={styles.primary}
                      disabled={busy || licence === (c.licence || '')}
                      onClick={() => save('/api/settings/tenant/licence', { licence: licence || null, before: c.licence })}
                    >
                      Change licence
                    </button>
                  </div>
                )}
              </section>

              <section className={styles.card}>
                <h2>Tags</h2>
                <dl className={styles.facts}>
                  <div><dt>Tag number</dt><dd>{c.tag_number ?? '—'}</dd></div>
                  <div><dt>Tag suffix</dt><dd>{c.tag_suffix ? <code>{c.tag_suffix}</code> : '—'}</dd></div>
                  <div><dt>Legacy packhouse</dt><dd>{c.legacy_packhouse_id != null ? `${c.legacy_name} (${c.legacy_packhouse_id})` : 'None'}</dd></div>
                </dl>
                {legacyError && <p className={styles.error}>{legacyError}</p>}
                <div className={styles.inline}>
                  <label className={styles.field}>
                    Legacy packhouse ID
                    <select value={legacy} onChange={(e) => setLegacy(e.target.value)} disabled={busy}>
                      <option value="">None (its own number)</option>
                      {packhouses
                        .filter((p) => p.packhouse_id >= 0 && (!p.client || p.client.id === id))
                        .map((p) => (
                          <option key={p.packhouse_id} value={p.packhouse_id}>
                            {p.packhouse_id} {p.name}
                          </option>
                        ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className={styles.primary}
                    disabled={busy || legacy === (c.legacy_packhouse_id == null ? '' : String(c.legacy_packhouse_id))}
                    onClick={link}
                    title="The client's tags then end in this number, so the old system's tags are its own"
                  >
                    {legacy === '' ? 'Unlink' : 'Link'}
                  </button>
                </div>
                <p className={styles.hint}>
                  Tags printed with the earlier number stop being this client's; they go to whichever client scanned them first.
                </p>
              </section>

              {c.legacy_packhouse_id != null && <LegacyImportCard clientId={id} onImported={load} />}

              <section className={styles.card}>
                <h2>Sign-ins</h2>
                <div className={styles.tableCard}>
                  {c.sign_ins.length === 0 ? (
                    <div className={styles.empty}>No sign-ins yet, besides Dataphyll's own.</div>
                  ) : (
                    <table className={styles.table}>
                      <thead><tr><th>Email</th><th>Role</th><th>Signed in</th><th>Added</th></tr></thead>
                      <tbody>
                        {c.sign_ins.map((s) => (
                          <tr key={s.email}>
                            <td>{s.email}</td>
                            <td>{s.role}</td>
                            <td>{s.signed_in ? 'Yes' : 'Not yet'}</td>
                            <td>{dateText(s.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>

              <section className={styles.card}>
                <h2>Licence history</h2>
                <div className={styles.tableCard}>
                  {c.licence_history.length === 0 ? (
                    <div className={styles.empty}>Never licensed.</div>
                  ) : (
                    <table className={styles.table}>
                      <thead><tr><th>From</th><th>Licence</th><th>Recorded by</th></tr></thead>
                      <tbody>
                        {c.licence_history.map((h, i) => (
                          <tr key={i}>
                            <td>{h.known ? dateTimeText(h.changed_at) : 'Before records began'}</td>
                            <td>{licenceText(h.licence)}</td>
                            <td>{h.source.replace(/_/g, ' ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>

              <section className={styles.card}>
                <h2>Changes</h2>
                <div className={styles.tableCard}>
                  {c.changes.length === 0 ? (
                    <div className={styles.empty}>No changes recorded.</div>
                  ) : (
                    <table className={styles.table}>
                      <thead><tr><th>When</th><th>Change</th><th>By</th><th>Before</th><th>After</th></tr></thead>
                      <tbody>
                        {c.changes.map((ch, i) => (
                          <tr key={i}>
                            <td>{dateTimeText(ch.at)}</td>
                            <td>{KIND[ch.kind] || ch.kind}</td>
                            <td>{ch.actor || '—'}</td>
                            <td>{valueText(ch.before)}</td>
                            <td>{valueText(ch.after)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </PageBody>
    </>
  )
}
